'use client'

import {usePathname, useSearchParams} from 'next/navigation'
import {useMemo} from 'react'

type PreviewBarProps = {
  siteKey?: string
  studioUrl?: string
}

export function PreviewBar({siteKey, studioUrl}: PreviewBarProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const query = searchParams?.toString()
  const currentPath = pathname ? `${pathname}${query ? `?${query}` : ''}` : '/'
  const exitHref = `/api/draft-mode/disable?redirect=${encodeURIComponent(currentPath)}`
  const editHref = useMemo(() => {
    const base = (studioUrl || '').trim()
    if (!base) return null
    try {
      const url = new URL(base)
      url.pathname = '/presentation'
      url.searchParams.set('preview', currentPath)
      return url.toString()
    } catch {
      return null
    }
  }, [studioUrl, currentPath])

  const inIframe = (() => {
    if (typeof window === 'undefined') return false
    try {
      return window.self !== window.top
    } catch {
      return true
    }
  })()
  const inSanityPreviewContext = searchParams?.has('sanity-preview-perspective') || false
  const shouldHide = inIframe || inSanityPreviewContext

  return (
    <div
      suppressHydrationWarning
      className={`sticky top-[var(--environment-bar-height,0px)] z-[110] border-b border-black/10 bg-amber-100/95 text-amber-950 backdrop-blur ${
        shouldHide ? 'hidden' : ''
      }`}
    >
      <div className="container flex min-h-10 items-center justify-between gap-3 px-4 text-xs md:text-sm">
        <p className="truncate font-medium">
          Preview mode (Drafts){siteKey ? ` - ${siteKey}` : ''}
        </p>
        <div className="flex items-center gap-3">
          {editHref ? (
            <a
              className="shrink-0 underline underline-offset-2 hover:no-underline"
              href={editHref}
              target="_blank"
              rel="noreferrer"
            >
              Edit in Sanity
            </a>
          ) : null}
          <a className="shrink-0 underline underline-offset-2 hover:no-underline" href={exitHref}>
            Exit preview
          </a>
        </div>
      </div>
    </div>
  )
}
